/**
 * Simulator Types and Interfaces
 * BizClick WMS Warehouse Simulator v6.0
 */

// ============================================================================
// Pre-Flight Validation Types
// ============================================================================

export type ValidationCategory = 'KRITISK' | 'PÅKREVD' | 'ANBEFALT' | 'VALGFRITT';
export type ValidationStatus = 'pass' | 'fail' | 'warning' | 'info';

export interface PreFlightValidation {
  category: ValidationCategory;
  name: string;
  status: ValidationStatus;
  message: string;
  canProceed: boolean;
  canAutoFix: boolean;
  fixAction?: () => Promise<void>;
}

export interface PreFlightReport {
  validations: PreFlightValidation[];
  canStart: boolean;
  criticalErrors: number;
  warnings: number;
  passed: number;
  timestamp: Date;
}

// ============================================================================
// Workflow Types
// ============================================================================

export type WorkflowType = 'picking' | 'packing' | 'receiving' | 'putaway' | 'counting' | 'transfer' | 'correction' | 'posdeduction';

export interface SimulationResult {
  workflowType: WorkflowType;
  success: boolean;
  successCount: number;
  errorCount: number;
  errors: string[];
  message?: string;
  duration: number;
  startTime: Date;
  endTime: Date;
}

// ============================================================================
// Configuration Types
// ============================================================================

export interface PickingWorkflowConfig {
  enabled: boolean;
  maxListsToProcess: number;
  ordersToGenerate: number;
  sendToPacking: boolean;
  usePallets: boolean;
  ruleGroupId: number; // Rule group to use for creating picking lists
}

export interface PackingWorkflowConfig {
  enabled: boolean;
  maxGroupsToProcess: number;
  packagesPerGroup: number;
  requirePackageConfirmation: boolean;
}

export interface ReceivingWorkflowConfig {
  enabled: boolean;
  minimumOrders: number;
  ordersToGenerate: number;
  maxOrdersToProcess: number;
  receivingBinCode: string;
  createPutAway: boolean;
}

export interface PutAwayWorkflowConfig {
  enabled: boolean;
  maxTasksToProcess: number;
  defaultPickBinCode: string;
}

export interface CountingWorkflowConfig {
  enabled: boolean;
  maxItems: number;
  errorRate: number;
  autoExport: boolean;
}

export interface TransferWorkflowConfig {
  enabled: boolean;
  maxTransfers: number;
  minimumQuantity: number;
  transferQuantity: number;
}

export interface CorrectionWorkflowConfig {
  enabled: boolean;
  maxCorrections: number;
  adjustmentInProbability: number; // 0-1, probability of adding items vs removing
  maxAdjustmentQuantity: number;
}

export interface PosDeductionWorkflowConfig {
  enabled: boolean;
  maxDeductions: number;
  maxQuantityPerDeduction: number;
  includeOverQuantityTests: boolean; // Test InsufficientQuantity-scenario
}

export interface SafetyConfig {
  maxTransactionsPerSession: number;
  maxDurationMinutes: number;
  lockCleanupIntervalMinutes: number;
  maxLockAgeMinutes: number;
  maxConcurrentSessions: number;
  cooldownBetweenSessionsMs: number;
}

export interface TimingConfig {
  walkTimeMs: number;
  scanTimeMs: number;
  pickTimeMs: number;
  packTimeMs: number;
  countTimeMs: number;
  receiveTimeMs: number;
  placeTimeMs: number;
}

export interface WorkflowsConfig {
  picking: PickingWorkflowConfig;
  packing: PackingWorkflowConfig;
  receiving: ReceivingWorkflowConfig;
  putaway: PutAwayWorkflowConfig;
  counting: CountingWorkflowConfig;
  transfer: TransferWorkflowConfig;
  correction: CorrectionWorkflowConfig;
  posdeduction: PosDeductionWorkflowConfig;
}

export interface BatchSerialConfig {
  enabled: boolean; // Global on/off for batch/serial in simulator
}

// ============================================================================
// Demo Mode Types (B1-B2-C7 demo-fundamentet)
// Alt her er OPT-IN: uten `demo`-config (eller demo.enabled=false) er simulator-
// oppførselen bit-identisk med før — ingen eksisterende flyt påvirkes.
// ============================================================================

export interface DemoOperatorRef {
  id: string;   // Operator.OperatorID i demo-tenanten (f.eks. 'DEMO01')
  name: string; // Visningsnavn (f.eks. 'Jonas Berg')
}

export interface DemoConfig {
  enabled: boolean;
  /**
   * Tenant-vakt: companyNo MÅ stå i denne lista for at simulatoren skal starte
   * i demo-modus. Hindrer at demo-profiler ved uhell kjøres mot feil tenant.
   */
  allowedCompanies: string[];
  /**
   * Navngitte operatører fra demo-tenanten (demo-manifest.json). Når satt brukes
   * disse i stedet for genererte 'SIM-OP-N'-operatører, slik at PickedBy i
   * OrderLine matcher seedede operatører og leaderboard viser ekte navn.
   */
  operators?: DemoOperatorRef[];
}

export type DemoTempoProfile = 'rolig' | 'normal' | 'travel';

interface DemoTempoSettings {
  operatorCount: number;
  workflowIntervalMs: number;
  maxDurationMinutes: number;
  ordersToGenerate: number;
  /** Kun disse workflow-typene aktiveres i demo-profilen */
  enabledWorkflows: WorkflowType[];
}

/**
 * Tempo-presets for demo (B2 «levende lager»). Måltall: rolig ≈ 1-2 hendelser/min
 * (bakgrunnspuls), normal ≈ 5-8/min, travel ≈ 15-20/min — godt innenfor
 * rate-limit (300 req/min) og safety-grensene.
 */
export const DEMO_TEMPO_PROFILES: Record<DemoTempoProfile, DemoTempoSettings> = {
  rolig:  { operatorCount: 3,  workflowIntervalMs: 10000, maxDurationMinutes: 90, ordersToGenerate: 2, enabledWorkflows: ['picking', 'packing'] },
  normal: { operatorCount: 6,  workflowIntervalMs: 5000,  maxDurationMinutes: 90, ordersToGenerate: 4, enabledWorkflows: ['picking', 'packing', 'receiving', 'putaway'] },
  travel: { operatorCount: 10, workflowIntervalMs: 2500,  maxDurationMinutes: 90, ordersToGenerate: 6, enabledWorkflows: ['picking', 'packing', 'receiving', 'putaway'] }
};

/**
 * Bygg en komplett SimulatorConfig fra en base-config + demo-tempo-profil.
 * Ren funksjon — muterer ikke base. ruleGroupId, companyNo, bins osv. beholdes
 * fra base (de er tenant-spesifikke og kommer fra demo-manifestet/brukeren).
 */
export const applyDemoTempoProfile = (
  base: SimulatorConfig,
  profile: DemoTempoProfile
): SimulatorConfig => {
  const p = DEMO_TEMPO_PROFILES[profile];
  const workflows = { ...base.workflows };
  (Object.keys(workflows) as WorkflowType[]).forEach(wf => {
    const enabled = p.enabledWorkflows.includes(wf);
    if (wf === 'picking') {
      workflows.picking = { ...workflows.picking, enabled, ordersToGenerate: p.ordersToGenerate };
    } else {
      workflows[wf] = { ...workflows[wf], enabled } as never;
    }
  });
  return {
    ...base,
    operatorCount: p.operatorCount,
    workflowIntervalMs: p.workflowIntervalMs,
    safety: { ...base.safety, maxDurationMinutes: p.maxDurationMinutes },
    workflows
  };
};

export interface SimulatorConfig {
  companyNo: string;
  warehouseId: string;
  operatorGroupId: string;
  operatorCount: number;
  workflowIntervalMs: number;
  safety: SafetyConfig;
  workflows: WorkflowsConfig;
  timing: TimingConfig;
  batchSerialConfig: BatchSerialConfig;
  /** Valgfri demo-modus (B1-B2-C7). Utelatt/disabled = eksakt samme oppførsel som før. */
  demo?: DemoConfig;
}

// ============================================================================
// Metrics Types
// ============================================================================

export interface WorkflowMetrics {
  workflowType: WorkflowType;
  successCount: number;
  errorCount: number;
  totalDuration: number;
  averageDuration: number;
  lastRun?: Date;
}

export interface SimulatorMetrics {
  totalTransactions: number;
  requestsPerSecond: number;
  averageResponseTime: number;
  errorRate: number;
  lockedPLists: number;
  startTime: Date;
  elapsedTime: number;
  workflows: Record<WorkflowType, WorkflowMetrics>;
}

// ============================================================================
// Virtual Operator Types
// ============================================================================

export type OperatorStatus = 'idle' | 'busy' | 'error';
export type OperatorSpecialization = 'picking' | 'packing' | 'receiving' | 'putaway' | 'counting' | 'transfer' | 'correction' | 'posdeduction' | 'general';

export interface VirtualOperatorInfo {
  id: string;
  name: string;
  status: OperatorStatus;
  specialization: OperatorSpecialization;
  currentTask?: string;
  currentWorkflow?: WorkflowType;
  tasksCompleted: number;
  errors: number;
}

// ============================================================================
// Lock Types
// ============================================================================

export interface LockInfo {
  pListId: number;
  operatorId: string;
  lockedAt: Date;
}

// ============================================================================
// Simulator State
// ============================================================================

export type SimulatorStatus = 'idle' | 'validating' | 'running' | 'paused' | 'stopping' | 'stopped' | 'error';

export interface SimulatorState {
  status: SimulatorStatus;
  config: SimulatorConfig;
  preFlightReport?: PreFlightReport;
  metrics: SimulatorMetrics;
  operators: VirtualOperatorInfo[];
  errors: string[];
  lastError?: string;
}

// ============================================================================
// Default Configuration
// ============================================================================

export const DEFAULT_SIMULATOR_CONFIG: SimulatorConfig = {
  companyNo: '',
  warehouseId: '',
  operatorGroupId: '',
  operatorCount: 5,
  workflowIntervalMs: 2000,

  safety: {
    maxTransactionsPerSession: 10000,
    maxDurationMinutes: 120,
    lockCleanupIntervalMinutes: 5,
    maxLockAgeMinutes: 30,
    maxConcurrentSessions: 3,
    cooldownBetweenSessionsMs: 30000
  },

  workflows: {
    picking: {
      enabled: true,
      maxListsToProcess: 10,
      ordersToGenerate: 5,
      sendToPacking: true,
      usePallets: false,
      ruleGroupId: 0 // Must be set by user - 0 means not configured
    },
    packing: {
      enabled: true,
      maxGroupsToProcess: 5,
      packagesPerGroup: 2,
      requirePackageConfirmation: true
    },
    receiving: {
      enabled: true,
      minimumOrders: 3,
      ordersToGenerate: 5,
      maxOrdersToProcess: 5,
      receivingBinCode: 'RCV-01',
      createPutAway: true
    },
    putaway: {
      enabled: true,
      maxTasksToProcess: 10,
      defaultPickBinCode: 'A-01-01'
    },
    counting: {
      enabled: false,
      maxItems: 20,
      errorRate: 0.1,
      autoExport: true
    },
    transfer: {
      enabled: false,
      maxTransfers: 5,
      minimumQuantity: 5,
      transferQuantity: 2
    },
    correction: {
      enabled: false,
      maxCorrections: 5,
      adjustmentInProbability: 0.5,
      maxAdjustmentQuantity: 5
    },
    posdeduction: {
      enabled: false,
      maxDeductions: 10,
      maxQuantityPerDeduction: 5,
      includeOverQuantityTests: true
    }
  },

  timing: {
    walkTimeMs: 3500,
    scanTimeMs: 800,
    pickTimeMs: 1200,
    packTimeMs: 2000,
    countTimeMs: 4000,
    receiveTimeMs: 2000,
    placeTimeMs: 800
  },

  batchSerialConfig: {
    enabled: false
  }
};

// ============================================================================
// Initial Metrics
// ============================================================================

export const createInitialMetrics = (): SimulatorMetrics => ({
  totalTransactions: 0,
  requestsPerSecond: 0,
  averageResponseTime: 0,
  errorRate: 0,
  lockedPLists: 0,
  startTime: new Date(),
  elapsedTime: 0,
  workflows: {
    picking: createWorkflowMetrics('picking'),
    packing: createWorkflowMetrics('packing'),
    receiving: createWorkflowMetrics('receiving'),
    putaway: createWorkflowMetrics('putaway'),
    counting: createWorkflowMetrics('counting'),
    transfer: createWorkflowMetrics('transfer'),
    correction: createWorkflowMetrics('correction'),
    posdeduction: createWorkflowMetrics('posdeduction')
  }
});

export const createWorkflowMetrics = (workflowType: WorkflowType): WorkflowMetrics => ({
  workflowType,
  successCount: 0,
  errorCount: 0,
  totalDuration: 0,
  averageDuration: 0
});
